import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axios';
import toast from 'react-hot-toast';
import FilterDropdown from '../components/FilterDropdown';
import {
    MessageSquare, Phone, Calendar, Trash2, User,
    Search, Filter, ChevronRight, ChevronDown, X, UserCircle, Bot, Clock,
    RefreshCw, MapPin, ClipboardList, AlertCircle, Plus
} from 'lucide-react';

const LeadManagement = () => {
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showConsultationModal, setShowConsultationModal] = useState(false);
    const [consultationLead, setConsultationLead] = useState(null);
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalLeads, setTotalLeads] = useState(0);
    const [statusFilter, setStatusFilter] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);
    const [showChatModal, setShowChatModal] = useState(false);
    const [salespersons, setSalespersons] = useState([]);
    const [defaultEmployeeId, setDefaultEmployeeId] = useState('');
    const [isSavingDefault, setIsSavingDefault] = useState(false);

    // Manual lead creation state
    const [showAddLeadModal, setShowAddLeadModal] = useState(false);
    const [newLeadName, setNewLeadName] = useState('');
    const [newLeadPhone, setNewLeadPhone] = useState('');
    const [newLeadEmail, setNewLeadEmail] = useState('');
    const [newLeadLocation, setNewLeadLocation] = useState('');
    const [newLeadDate, setNewLeadDate] = useState('');
    const [newLeadTime, setNewLeadTime] = useState('');
    const [newLeadEmployeeId, setNewLeadEmployeeId] = useState('');
    const [newLeadRequirements, setNewLeadRequirements] = useState([]);
    const [newLeadNotes, setNewLeadNotes] = useState('');
    const [isCreatingLead, setIsCreatingLead] = useState(false);
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const employeeDropdownRef = React.useRef(null);

    // Close custom employee dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (employeeDropdownRef.current && !employeeDropdownRef.current.contains(e.target)) {
                setShowEmployeeDropdown(false);
            }
        };
        if (showEmployeeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showEmployeeDropdown]);

    const fetchLeads = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        if (silent) setIsRefreshing(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/admin/leads', {
                params: {
                    page: currentPage,
                    limit: pageSize,
                    status: statusFilter,
                    search: searchTerm
                },
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('🔄 Fetched leads:', response.data.leads.length);
            setLeads(response.data.leads);
            setTotalPages(response.data.pagination.totalPages);
            setTotalLeads(response.data.pagination.totalLeads);
        } catch (error) {
            console.error('Fetch leads error:', error);
            if (!silent) toast.error('Failed to fetch leads');
        } finally {
            if (!silent) setLoading(false);
            if (silent) {
                // Keep the indicator for a short moment to show it happened
                setTimeout(() => setIsRefreshing(false), 2000);
            }
        }
    }, [currentPage, pageSize, statusFilter, searchTerm]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    // Debounced search effect
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (currentPage !== 1) {
                setCurrentPage(1);
            } else {
                fetchLeads();
            }
            setSelectedLeads([]); // Clear selection when search changes
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Clear selection when filters change (but keep across pagination pages)
    useEffect(() => {
        setSelectedLeads([]);
    }, [statusFilter]);

    const fetchSalespersonsAndSettings = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const [salespersonsRes, settingsRes] = await Promise.all([
                axios.get('/admin/salespersons', { headers: { Authorization: `Bearer ${token}` } }),
                axios.get('/admin/company-settings', { headers: { Authorization: `Bearer ${token}` } })
            ]);
            
            const activeSalespersons = salespersonsRes.data.filter(sp => sp.is_active !== false);
            setSalespersons(activeSalespersons);
            
            if (settingsRes.data && settingsRes.data.defaultSalesperson) {
                const defaultId = typeof settingsRes.data.defaultSalesperson === 'object'
                    ? settingsRes.data.defaultSalesperson._id || settingsRes.data.defaultSalesperson.id
                    : settingsRes.data.defaultSalesperson;
                setDefaultEmployeeId(defaultId || '');
            }
        } catch (error) {
            console.error('Error fetching salespersons or settings:', error);
        }
    }, []);

    useEffect(() => {
        fetchSalespersonsAndSettings();
    }, [fetchSalespersonsAndSettings]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchLeads(true); // silent refresh
        }, 30000); // 30 seconds

        return () => clearInterval(interval);
    }, [fetchLeads]);

    const handleSaveDefaultEmployee = async () => {
        setIsSavingDefault(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put('/admin/company-settings', 
                { defaultSalesperson: defaultEmployeeId || null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Default site visit consultant updated successfully!');
            fetchSalespersonsAndSettings();
        } catch (error) {
            console.error('Error saving default settings:', error);
            toast.error('Failed to update default consultant');
        } finally {
            setIsSavingDefault(false);
        }
    };

    const handleOpenAddLeadModal = () => {
        setNewLeadName('');
        setNewLeadPhone('');
        setNewLeadEmail('');
        setNewLeadLocation('');
        setNewLeadDate('');
        setNewLeadTime('');
        setNewLeadEmployeeId(defaultEmployeeId || '');
        setNewLeadRequirements([]);
        setNewLeadNotes('');
        setShowAddLeadModal(true);
    };

    const handleToggleRequirement = (category) => {
        setNewLeadRequirements(prev => 
            prev.includes(category) 
                ? prev.filter(c => c !== category) 
                : [...prev, category]
        );
    };

    const handleCreateLead = async (e) => {
        e.preventDefault();
        
        if (!newLeadPhone) {
            toast.error('Phone number is required');
            return;
        }

        const cleanPhone = newLeadPhone.replace(/\D/g, '');
        if (cleanPhone.length < 10) {
            toast.error('Please provide a valid 10-digit phone number');
            return;
        }

        setIsCreatingLead(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/admin/leads', {
                name: newLeadName,
                phone: cleanPhone,
                email: newLeadEmail,
                location: newLeadLocation,
                date: newLeadDate,
                time: newLeadTime,
                assignedEmployee: newLeadEmployeeId || null,
                requirements: newLeadRequirements,
                notes: newLeadNotes
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success(response.data.message || 'Lead created successfully!');
            setShowAddLeadModal(false);
            fetchLeads();
        } catch (error) {
            console.error('Create lead error:', error);
            const errMsg = error.response?.data?.error || 'Failed to create lead';
            toast.error(errMsg);
        } finally {
            setIsCreatingLead(false);
        }
    };

    const handleAssignEmployee = async (leadId, employeeId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`/admin/leads/${leadId}`, 
                { assignedEmployee: employeeId || null },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Employee assigned successfully');
            fetchLeads();
        } catch (error) {
            console.error('Reassign lead error:', error);
            toast.error('Failed to reassign employee');
        }
    };


    const handleUpdateStatus = async (id, newStatus) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`/admin/leads/${id}`, { status: newStatus }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Status updated');
            fetchLeads();
        } catch (error) {
            toast.error('Failed to update status');
        }
    };

    const handleDeleteLead = async (id) => {
        if (window.confirm('Are you sure you want to delete this lead?')) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`/admin/leads/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Lead deleted');
                setSelectedLeads(prev => prev.filter(leadId => leadId !== id));
                fetchLeads();
            } catch (error) {
                toast.error('Failed to delete lead');
            }
        }
    };

    const handleBulkDelete = async () => {
        if (selectedLeads.length === 0) return;

        if (window.confirm(`Are you sure you want to delete ${selectedLeads.length} selected leads? This action cannot be undone.`)) {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.post('/admin/leads/bulk-delete', 
                    { ids: selectedLeads },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                
                toast.success(response.data.message || 'Leads deleted successfully');
                setSelectedLeads([]);
                fetchLeads();
            } catch (error) {
                console.error('Bulk delete error:', error);
                toast.error(error.response?.data?.error || 'Failed to delete selected leads');
            }
        }
    };

    const toggleSelectAll = () => {
        const currentPageIds = leads.map(l => l.id);
        const areAllCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedLeads.includes(id));

        if (areAllCurrentPageSelected) {
            // Deselect all on current page
            setSelectedLeads(prev => prev.filter(id => !currentPageIds.includes(id)));
        } else {
            // Select all on current page (avoiding duplicates)
            setSelectedLeads(prev => {
                const newSelection = [...prev];
                currentPageIds.forEach(id => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                return newSelection;
            });
        }
    };

    const toggleSelectLead = (id) => {
        setSelectedLeads(prev => 
            prev.includes(id) 
                ? prev.filter(i => i !== id) 
                : [...prev, id]
        );
    };

    const viewChatHistory = async (lead) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`/admin/leads/${lead.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedLead(response.data);
            setShowChatModal(true);
        } catch (error) {
            toast.error('Failed to load chat history');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };


    const employeeOptions = [
        { value: '', label: '-- Select Default Assigned Employee --' },
        ...salespersons.map(sp => ({ value: sp.id, label: sp.name }))
    ];

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0A5F38]"></div>
                <p className="mt-4 text-gray-600 font-medium">Loading leads...</p>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">
                {/* Header Actions */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2.5">
                            <h2 className="text-2xl font-black text-slate-900 tracking-tight">Lead Management</h2>
                            {isRefreshing ? (
                                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-md border border-emerald-100 animate-pulse text-[10px] font-extrabold uppercase tracking-wider">
                                    <RefreshCw className="h-3 w-3 animate-spin" />
                                    Syncing
                                </div>
                            ) : (
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md border border-slate-200 text-[10px] font-extrabold">
                                    {totalLeads} Leads
                                </span>
                            )}
                        </div>
                        <p className="text-slate-500 text-xs mt-1 font-medium">Track and manage potential customers from Chatbot</p>
                    </div>

                    {/* Sleek Default Employee Quick Setting */}
                    <div className="flex flex-wrap items-center gap-4 bg-slate-50/60 border border-slate-200/60 p-3 rounded-2xl max-w-lg shrink-0">
                        <div className="flex flex-col">
                            <span className="text-[10px] font-extrabold text-[#0A5F38] uppercase tracking-wider">Default Employee</span>
                            <span className="text-xs text-slate-500 font-semibold mt-0.5">Auto-assign new leads:</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <FilterDropdown
                                value={defaultEmployeeId}
                                onChange={(e) => setDefaultEmployeeId(e.target.value)}
                                options={employeeOptions}
                                className="min-w-[200px]"
                            />
                            <button
                                onClick={handleSaveDefaultEmployee}
                                disabled={isSavingDefault}
                                className="px-4 py-3 bg-[#0A5F38] hover:bg-[#0A5F38]/90 disabled:opacity-50 text-white rounded-xl font-bold text-xs transition-all shadow-md shadow-[#0A5F38]/10 whitespace-nowrap"
                            >
                                {isSavingDefault ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Unified Toolbar Card */}
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* Left Column: Search & Filter */}
                    <div className="flex flex-wrap items-center gap-3 flex-grow max-w-3xl">
                        {/* Search */}
                        <div className="relative flex-grow md:max-w-xs">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search phone, name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent transition-all outline-none text-sm font-semibold text-slate-800"
                            />
                        </div>

                        {/* Filter Dropdown */}
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5">
                            <Filter className="h-4 w-4 text-slate-400" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="bg-transparent border-none outline-none text-xs font-bold text-slate-700 cursor-pointer"
                            >
                                <option value="All">All Status</option>
                                <option value="New">New</option>
                                <option value="Exploring">Exploring</option>
                                <option value="Interested">Interested</option>
                                <option value="Request Call Back">Request Call Back</option>
                                <option value="Requirement Not Matched">Unmatched Requirement</option>
                                <option value="Not Interested">Not Interested</option>
                                <option value="Site Visit Requested">Scheduling</option>
                                <option value="Consultation Booked">Consultation Booked</option>
                                <option value="Followed Up">Followed Up</option>
                                <option value="Converted">Converted</option>
                                <option value="Closed">Closed</option>
                            </select>
                        </div>

                        {/* Bulk Actions */}
                        {selectedLeads.length > 0 && (
                            <button
                                onClick={handleBulkDelete}
                                className="flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 border border-red-100 rounded-xl hover:bg-red-100 transition-all font-bold text-xs"
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                                Delete Selected ({selectedLeads.length})
                            </button>
                        )}
                    </div>

                    {/* Right Column: Main Add Button CTA */}
                    <div className="shrink-0 flex justify-end">
                        <button
                            onClick={handleOpenAddLeadModal}
                            className="flex items-center gap-2 px-5 py-2.5 bg-[#0A5F38] text-white rounded-xl hover:bg-[#0A5F38]/90 transition-all font-bold text-sm shadow-md shadow-[#0A5F38]/10"
                        >
                            <Plus className="h-4 w-4 text-white" />
                            Add New Lead
                        </button>
                    </div>
                </div>


                {/* Leads Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50/50 border-b border-slate-100">
                                    <th className="px-6 py-4 w-10">
                                        <input
                                            type="checkbox"
                                            checked={leads.length > 0 && leads.every(l => selectedLeads.includes(l.id))}
                                            onChange={toggleSelectAll}
                                            className="w-4 h-4 rounded border-slate-300 text-[#0A5F38] focus:ring-[#0A5F38]"
                                        />
                                    </th>
                                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Lead Info</th>
                                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Requirements</th>
                                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Default Assigned Employee</th>
                                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {leads && leads.length > 0 ? (
                                    leads.map((lead) => (
                                        <tr 
                                            key={lead.id} 
                                            className={`hover:bg-slate-50/30 transition-colors group ${selectedLeads.includes(lead.id) ? 'bg-emerald-50/50' : ''}`}
                                        >
                                            <td className="px-6 py-4">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeads.includes(lead.id)}
                                                    onChange={() => toggleSelectLead(lead.id)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="w-4 h-4 rounded border-slate-300 text-[#0A5F38] focus:ring-[#0A5F38]"
                                                />
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-0.5">
                                                    <div className="font-bold text-slate-900 flex items-center gap-2">
                                                        {lead.phone}
                                                        {lead.status === 'New' && (
                                                            <span className="flex h-2 w-2 rounded-full bg-blue-500" title="New message"></span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-xs text-slate-500">{lead.name || 'Guest User'}</span>
                                                    {lead.source === 'gallery' && (
                                                            <span
                                                                title="Submitted from product gallery"
                                                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-teal-50 border border-teal-200 text-teal-700 text-[10px] font-bold cursor-default"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                                                                Gallery
                                                            </span>
                                                        )}
                                                        {(lead.source === 'manual' || (
                                                            lead.source !== 'chatbot' &&
                                                            lead.source !== 'gallery' &&
                                                            (!lead.chat_history || lead.chat_history.length === 0) &&
                                                            lead.consultation_details
                                                        )) && (
                                                            <span
                                                                title="Manually added by admin"
                                                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold cursor-default"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                                                                Manual
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-wrap gap-1 max-w-xs">
                                                    {lead.requirements && lead.requirements.length > 0 ? (
                                                        lead.requirements.map((req, i) => (
                                                            <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold border border-slate-200">
                                                                {req}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="text-slate-400 text-xs italic">No specific products mentioned</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                {lead.status === 'Consultation Booked' ? (
                                                    <div className="flex flex-col gap-1.5">
                                                        <select
                                                            value={lead.assigned_employee?.id || ''}
                                                            onChange={(e) => handleAssignEmployee(lead.id, e.target.value)}
                                                            className="text-xs font-bold px-2.5 py-1.5 rounded-xl bg-[#F9F9F7] border border-[#C5A57D] outline-none focus:ring-2 focus:ring-[#0A5F38] cursor-pointer text-[#0A5F38] min-w-[150px] transition-colors hover:border-[#0A5F38]"
                                                        >
                                                            <option value="" className="text-slate-500 font-normal">Unassigned</option>
                                                            {salespersons.map(sp => (
                                                                <option key={sp.id} value={sp.id} className="text-slate-800 font-semibold">{sp.name}</option>
                                                            ))}
                                                        </select>
                                                        
                                                        {/* Site Visit Status Badge */}
                                                        {(() => {
                                                            const status = lead.site_visit_status || 'Pending';
                                                            if (status === 'Site Visit Completed' || status === 'Completed') {
                                                                return (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-lg border border-emerald-200 text-[10px] font-bold self-start cursor-default">
                                                                        ✓ Visit Completed
                                                                    </span>
                                                                );
                                                            } else if (status === 'Site Working Completed 100%') {
                                                                return (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-green-50 text-green-700 rounded-lg border border-green-200 text-[10px] font-bold self-start cursor-default">
                                                                        ✓ 100% Completed
                                                                    </span>
                                                                );
                                                            } else if (status === 'Site Working In Progress') {
                                                                return (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-lg border border-blue-200 text-[10px] font-bold self-start cursor-default animate-pulse">
                                                                        ⟳ Working In Progress
                                                                    </span>
                                                                );
                                                            } else {
                                                                return (
                                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-amber-50 text-amber-700 rounded-lg border border-amber-200 text-[10px] font-bold self-start cursor-default animate-pulse">
                                                                        ⟳ Pending Visit
                                                                    </span>
                                                                );
                                                            }
                                                        })()}
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-slate-400 italic font-medium">Not a site visit</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <select
                                                    value={lead.status}
                                                    onChange={(e) => handleUpdateStatus(lead.id, e.target.value)}
                                                    className={`text-[10px] font-bold px-2 py-1 rounded-full outline-none border cursor-pointer ${
                                                        lead.status === 'New' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                        lead.status === 'Exploring' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                                                        lead.status === 'Interested' ? 'bg-cyan-50 text-cyan-700 border-cyan-200' :
                                                        lead.status === 'Request Call Back' ? 'bg-teal-50 text-teal-700 border-teal-200' :
                                                        lead.status === 'Requirement Not Matched' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                                                        lead.status === 'Not Interested' ? 'bg-gray-50 text-gray-700 border-gray-200' :
                                                        lead.status === 'Site Visit Requested' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                        lead.status === 'Consultation Booked' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                                        lead.status === 'Followed Up' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                                        lead.status === 'Converted' ? 'bg-green-50 text-green-700 border-green-200' :
                                                        'bg-slate-50 text-slate-600 border-slate-200'
                                                    }`}
                                                >
                                                    <option value="New">New</option>
                                                    <option value="Exploring">Exploring</option>
                                                    <option value="Interested">Interested</option>
                                                    <option value="Request Call Back">Request Call Back</option>
                                                    <option value="Requirement Not Matched">Unmatched Requirement</option>
                                                    <option value="Not Interested">Not Interested</option>
                                                    <option value="Site Visit Requested">Scheduling</option>
                                                    <option value="Consultation Booked">Consultation Booked</option>
                                                    <option value="Followed Up">Followed Up</option>
                                                    <option value="Converted">Converted</option>
                                                    <option value="Closed">Closed</option>
                                                </select>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    {lead.status === 'Consultation Booked' && lead.consultation_details && (
                                                        <button
                                                            onClick={() => {
                                                                setConsultationLead(lead);
                                                                setShowConsultationModal(true);
                                                            }}
                                                            className="p-2 text-cyan-600 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-colors border border-cyan-100"
                                                            title="View Consultation Details"
                                                        >
                                                            <Calendar className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => viewChatHistory(lead)}
                                                        className="p-2 text-[#0A5F38] bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors border border-emerald-100"
                                                        title="View Chat History"
                                                    >
                                                        <MessageSquare className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteLead(lead.id)}
                                                        className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-100"
                                                        title="Delete Lead"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center">
                                                <User className="h-12 w-12 text-slate-200 mb-2" />
                                                <p className="text-slate-500 font-medium">No leads found matching your filters</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="text-sm text-slate-500 font-medium">
                            Showing <span className="text-slate-900 font-bold">{totalLeads === 0 ? 0 : (currentPage - 1) * pageSize + 1}</span> to{' '}
                            <span className="text-slate-900 font-bold">{Math.min(currentPage * pageSize, totalLeads)}</span> of{' '}
                            <span className="text-slate-900 font-bold">{totalLeads}</span> leads
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1 || loading}
                                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 font-bold text-xs hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Previous
                            </button>
                            
                            <div className="flex items-center gap-1">
                                {[...Array(totalPages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    if (
                                        pageNum === 1 ||
                                        pageNum === totalPages ||
                                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                    ) {
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`h-8 w-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                                                    currentPage === pageNum
                                                        ? 'bg-[#0A5F38] text-white shadow-md shadow-[#0A5F38]/20'
                                                        : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                                                }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    } else if (
                                        (pageNum === currentPage - 2 && pageNum > 1) ||
                                        (pageNum === currentPage + 2 && pageNum < totalPages)
                                    ) {
                                        return <span key={pageNum} className="text-slate-400 text-xs">...</span>;
                                    }
                                    return null;
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages || totalPages === 0 || loading}
                                className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 font-bold text-xs hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Chat History Modal */}
            {showChatModal && selectedLead && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-[#F8F5E6] rounded-3xl w-full max-w-2xl h-[80vh] flex flex-col shadow-2xl border border-[#C5A57D]/30 overflow-hidden animate-in fade-in zoom-in duration-200">
                        {/* Modal Header */}
                        <div className="bg-[#0A5F38] p-6 text-white flex items-center justify-between shadow-md">
                            <div className="flex items-center gap-4">
                                <div className="h-12 w-12 bg-white/20 rounded-2xl flex items-center justify-center text-xl font-bold backdrop-blur-md">
                                    {selectedLead.name ? selectedLead.name.charAt(0).toUpperCase() : <UserCircle className="h-6 w-6" />}
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold leading-none mb-1">{selectedLead.phone}</h3>
                                    <div className="flex items-center gap-2 text-white/70 text-sm font-medium">
                                        <span className="h-2 w-2 rounded-full bg-emerald-400"></span>
                                        {selectedLead.name || 'Chatbot Guest'}
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowChatModal(false)}
                                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Modal Content - Chat History */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-pattern flex flex-col">
                            {selectedLead.chat_history && selectedLead.chat_history.length > 0 ? (
                                selectedLead.chat_history.map((msg, i) => (
                                    <div key={i} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                                        <div className={`flex items-end gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                                            {/* Avatar */}
                                            <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 shadow-sm ${msg.role === 'user' ? 'bg-[#C5A57D] text-[#0A5F38]' : 'bg-[#0A5F38] text-white'
                                                }`}>
                                                {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                                            </div>

                                            {/* Message Bubble */}
                                            <div className={`relative p-3 rounded-2xl text-sm shadow-sm ${msg.role === 'user'
                                                ? 'bg-[#0A5F38] text-white rounded-tr-none border border-[#0A5F38]/10'
                                                : 'bg-white text-slate-800 rounded-tl-none border border-slate-200'
                                                }`}>
                                                <div className="whitespace-pre-wrap leading-relaxed truncate-content">
                                                    {typeof msg.content === 'string' ? msg.content : JSON.stringify(msg.content)}
                                                </div>
                                                <div className={`text-[10px] mt-1 font-medium ${msg.role === 'user' ? 'text-white/60 text-right' : 'text-slate-400'}`}>
                                                    {new Date(msg.ts || msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-slate-400 italic">
                                    <MessageSquare className="h-12 w-12 mb-3 opacity-20" />
                                    <p>No chat history recorded yet.</p>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer - Status Controls */}
                        <div className="p-6 bg-white border-t border-slate-100">
                                <div className="flex justify-end w-full">
                                    <button
                                        onClick={() => setShowChatModal(false)}
                                        className="px-6 py-2 bg-slate-100 text-slate-700 font-bold rounded-xl text-sm hover:bg-slate-200 transition-colors"
                                    >
                                        Close View
                                    </button>
                                </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .bg-pattern {
                    background-color: #F8F5E6;
                    background-image: radial-gradient(#C5A57D 0.5px, transparent 0.5px);
                    background-size: 20px 20px;
                }
                .bg-pattern::before {
                    content: "";
                    position: absolute;
                    top: 0; left: 0; right: 0; bottom: 0;
                    background-color: #F8F5E6;
                    opacity: 0.1;
                    z-index: -1;
                }
                .truncate-content {
                    max-width: 100%;
                    overflow-wrap: break-word;
                }
            `}</style>

            {/* Consultation Details Modal */}
            {showConsultationModal && consultationLead && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in duration-200">
                        {/* Modal Header */}
                        <div className="bg-[#0A5F38] p-6 text-white flex items-center justify-between shadow-md">
                            <div className="flex items-center gap-4">
                                <div className="h-12 w-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                                    <Calendar className="h-6 w-6" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold leading-none mb-1">Consultation Details</h3>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowConsultationModal(false)}
                                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Modal Content */}
                        <div className="p-6 space-y-6">
                            {/* Schedule Summary */}
                            <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-4 flex items-center gap-4">
                                <div className="h-10 w-10 bg-white rounded-xl flex items-center justify-center text-[#0A5F38] shadow-sm">
                                    <Clock className="h-5 w-5" />
                                </div>
                                <div>
                                    <div className="text-xs text-emerald-600 font-bold uppercase tracking-wider">Scheduled For</div>
                                    <div className="text-slate-900 font-bold">
                                        {consultationLead.consultation_details?.date && consultationLead.consultation_details?.time 
                                            ? `${consultationLead.consultation_details.date} at ${consultationLead.consultation_details.time}`
                                            : (consultationLead.consultation_details?.date || consultationLead.consultation_details?.time || 'Schedule not confirmed')
                                        }
                                    </div>
                                </div>
                            </div>

                            {/* Details Grid */}
                            <div className="grid grid-cols-1 gap-4">
                                {/* Removed Client Name as per user request */}
                                <div className="flex items-start gap-4">
                                    <div className="h-8 w-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400 shrink-0">
                                        <Phone className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="text-xs text-slate-500 font-medium">Contact Number</div>
                                        <div className="text-slate-900 font-bold">{consultationLead.consultation_details?.phone || consultationLead.phone}</div>
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="h-8 w-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400 shrink-0">
                                        <MapPin className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="text-xs text-slate-500 font-medium">Site Address</div>
                                        <div className="text-slate-900 font-bold leading-tight">
                                            {consultationLead.consultation_details?.location || 'Not specified'}
                                        </div>
                                        {consultationLead.consultation_details?.mapsLink && (
                                            <a 
                                                href={consultationLead.consultation_details.mapsLink} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-[#0A5F38] text-xs font-bold hover:underline mt-1 inline-block"
                                            >
                                                Open in Google Maps →
                                            </a>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-start gap-4">
                                    <div className="h-8 w-8 bg-slate-50 rounded-lg flex items-center justify-center text-slate-400 shrink-0">
                                        <ClipboardList className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <div className="text-xs text-slate-500 font-medium">Requirements</div>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {consultationLead.requirements && consultationLead.requirements.length > 0 ? (
                                                consultationLead.requirements.map((req, i) => (
                                                    <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold border border-slate-200">
                                                        {req}
                                                    </span>
                                                ))
                                            ) : (
                                                <span className="text-slate-400 text-xs italic">No requirements listed</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end">
                            <button
                                onClick={() => setShowConsultationModal(false)}
                                className="px-6 py-2 bg-white border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-100 transition-colors shadow-sm"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add New Lead Modal */}
            {showAddLeadModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-[#F8F5E6] rounded-3xl w-full max-w-3xl shadow-2xl border border-[#C5A57D]/30 overflow-hidden animate-in fade-in zoom-in duration-200 my-4">
                        {/* Modal Header */}
                        <div className="bg-[#0A5F38] py-4 px-6 text-white flex items-center justify-between shadow-md">
                            <div className="flex items-center gap-3">
                                <div className="h-9 w-9 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-md">
                                    <Plus className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-base font-bold leading-none mb-0.5">Create New Lead</h3>
                                    <p className="text-emerald-100 text-xs">Add manual lead and schedule site visit consultation</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowAddLeadModal(false)}
                                className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Modal Body — Two-Column Horizontal Layout */}
                        <form onSubmit={handleCreateLead}>
                            <div className="flex divide-x divide-slate-200/60">

                                {/* ── LEFT COLUMN ── */}
                                <div className="flex-1 p-5 space-y-4">
                                    {/* Customer Info */}
                                    <div>
                                        <h4 className="text-xs font-extrabold text-[#0A5F38] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                            <User className="h-3.5 w-3.5" />
                                            Customer Information
                                        </h4>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-600 mb-1">Customer Name <span className="text-red-500">*</span></label>
                                                <input
                                                    type="text"
                                                    value={newLeadName}
                                                    onChange={(e) => setNewLeadName(e.target.value)}
                                                    placeholder="e.g. John Doe"
                                                    required
                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent outline-none text-sm font-semibold text-slate-800 transition-all"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-600 mb-1">Phone Number <span className="text-red-500">*</span></label>
                                                <input
                                                    type="tel"
                                                    value={newLeadPhone}
                                                    onChange={(e) => setNewLeadPhone(e.target.value)}
                                                    placeholder="e.g. 9876543210"
                                                    required
                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent outline-none text-sm font-semibold text-slate-800 transition-all"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <hr className="border-slate-200/60" />

                                    {/* Default Assigned Employee */}
                                    <div>
                                        <p className="text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Employee</p>
                                        <div className="relative" ref={employeeDropdownRef}>
                                            {/* Trigger Button */}
                                            <button
                                                type="button"
                                                onClick={() => setShowEmployeeDropdown(prev => !prev)}
                                                className="w-full flex items-center justify-between px-4 py-2.5 border-2 border-[#0A5F38] rounded-2xl bg-white hover:bg-[#f0f9f4] transition-colors"
                                            >
                                                <span className="text-sm font-bold text-[#0A5F38] truncate">
                                                    {newLeadEmployeeId
                                                        ? (salespersons.find(sp => sp.id === newLeadEmployeeId)?.name || 'Employee')
                                                        : 'Use Default Salesperson'}
                                                </span>
                                                <ChevronDown className={`h-4 w-4 text-[#0A5F38] shrink-0 ml-2 transition-transform duration-200 ${showEmployeeDropdown ? 'rotate-180' : ''}`} />
                                            </button>

                                            {/* Dropdown Panel */}
                                            {showEmployeeDropdown && (
                                                <div className="absolute z-50 left-0 right-0 mt-1.5 bg-white border border-[#C5A57D]/40 rounded-2xl shadow-xl overflow-hidden">
                                                    <div className="max-h-48 overflow-y-auto">
                                                        {/* Default option */}
                                                        <div
                                                            onClick={() => { setNewLeadEmployeeId(''); setShowEmployeeDropdown(false); }}
                                                            className={`px-4 py-2.5 text-sm font-bold cursor-pointer transition-colors ${
                                                                newLeadEmployeeId === ''
                                                                    ? 'bg-[#e8f5ee] text-[#0A5F38]'
                                                                    : 'text-slate-600 hover:bg-slate-50'
                                                            }`}
                                                        >
                                                            Use Default Salesperson
                                                        </div>
                                                        {/* Employee options */}
                                                        {salespersons.map(sp => (
                                                            <div
                                                                key={sp.id}
                                                                onClick={() => { setNewLeadEmployeeId(sp.id); setShowEmployeeDropdown(false); }}
                                                                className={`px-4 py-2.5 text-sm cursor-pointer transition-colors ${
                                                                    newLeadEmployeeId === sp.id
                                                                        ? 'bg-[#e8f5ee] text-[#0A5F38] font-bold'
                                                                        : 'text-slate-700 hover:bg-slate-50 font-medium'
                                                                }`}
                                                            >
                                                                {sp.name}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <p className="text-[10px] text-slate-500 mt-1.5 font-medium">
                                            Defaults to the active default salesperson if left unselected.
                                        </p>
                                    </div>
                                </div>

                                {/* ── RIGHT COLUMN ── */}
                                <div className="flex-1 p-5 space-y-4">
                                    {/* Consultation & Site Visit */}
                                    <div>
                                        <h4 className="text-xs font-extrabold text-[#0A5F38] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                            <Calendar className="h-3.5 w-3.5" />
                                            Consultation &amp; Site Visit
                                        </h4>
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-600 mb-1">Preferred Date <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="date"
                                                        value={newLeadDate}
                                                        onChange={(e) => setNewLeadDate(e.target.value)}
                                                        required
                                                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent outline-none text-sm font-semibold text-slate-800 transition-all"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-bold text-slate-600 mb-1">Time Slot <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="text"
                                                        value={newLeadTime}
                                                        onChange={(e) => setNewLeadTime(e.target.value)}
                                                        placeholder="e.g. 11:00 AM"
                                                        required
                                                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent outline-none text-sm font-semibold text-slate-800 transition-all"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-600 mb-1">Site Location / Address</label>
                                                <textarea
                                                    value={newLeadLocation}
                                                    onChange={(e) => setNewLeadLocation(e.target.value)}
                                                    placeholder="Enter the complete site visit location address..."
                                                    rows={2}
                                                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#0A5F38] focus:border-transparent outline-none text-sm font-semibold text-slate-800 transition-all resize-none"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <hr className="border-slate-200/60" />

                                    {/* Requirements */}
                                    <div>
                                        <h4 className="text-xs font-extrabold text-[#0A5F38] uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                            <ClipboardList className="h-3.5 w-3.5" />
                                            Requirements
                                        </h4>
                                        <div className="flex flex-wrap gap-1.5">
                                            {['Living Room', 'Bedroom', 'Modular Kitchen', 'Wallpaper', 'Balcony', 'Ceiling', 'Bathroom', 'Wardrobe', 'Other'].map(category => {
                                                const isSelected = newLeadRequirements.includes(category);
                                                return (
                                                    <button
                                                        type="button"
                                                        key={category}
                                                        onClick={() => handleToggleRequirement(category)}
                                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all border ${
                                                            isSelected
                                                                ? 'bg-[#0A5F38] text-white border-[#0A5F38] shadow-sm shadow-[#0A5F38]/20'
                                                                : 'bg-white hover:bg-slate-50 text-slate-600 border-slate-200'
                                                        }`}
                                                    >
                                                        {category}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 py-4 bg-white/60 border-t border-slate-200/60 flex justify-end gap-2.5">
                                <button
                                    type="button"
                                    onClick={() => setShowAddLeadModal(false)}
                                    className="px-5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 font-bold rounded-xl text-sm transition-all shadow-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={isCreatingLead}
                                    className="px-6 py-2 bg-[#0A5F38] hover:bg-[#0A5F38]/90 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-all shadow-md shadow-[#0A5F38]/10"
                                >
                                    {isCreatingLead ? 'Saving...' : 'Save Lead'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default LeadManagement;
